/* Warehouse Gear Hub — datasheet product page behaviours.
   Loaded as a module by snippets/wgh-head.liquid. Follows the theme's
   base-component pattern so the variant-change events from
   product-variant-selector.js keep every panel element in sync. */
(async () => {
  const baseComponent = await import(window.theme.modules.baseComponent);
  const Base = baseComponent.default;

  /* ---- Tabs ------------------------------------------------------------ */
  if (!customElements.get('wgh-tabs')) {
    customElements.define('wgh-tabs', class extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        if (!this.tabs.length) return;
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i));
          tab.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
              e.preventDefault();
              const dir = e.key === 'ArrowRight' ? 1 : -1;
              const next = (i + dir + this.tabs.length) % this.tabs.length;
              this.select(next);
              this.tabs[next].focus();
            }
          });
        });
        const initial = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
        this.select(initial >= 0 ? initial : 0);
      }
      select(index) {
        this.tabs.forEach((tab, i) => {
          const on = i === index;
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.setAttribute('tabindex', on ? '0' : '-1');
          if (this.panels[i]) this.panels[i].hidden = !on;
        });
      }
    });
  }

  /* ---- Quantity stepper ------------------------------------------------ */
  if (!customElements.get('wgh-qty-stepper')) {
    customElements.define('wgh-qty-stepper', class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        this.dec = this.querySelector('[data-qty-dec]');
        this.inc = this.querySelector('[data-qty-inc]');
        if (!this.input) return;
        this.step = parseInt(this.input.getAttribute('step'), 10) || 1;
        this.min = parseInt(this.input.getAttribute('min'), 10) || 1;
        this.max = parseInt(this.input.getAttribute('max'), 10) || 999;
        this.dec?.addEventListener('click', () => this.set(this.value() - this.step));
        this.inc?.addEventListener('click', () => this.set(this.value() + this.step));
        this.input.addEventListener('change', () => this.set(this.value()));
        this.sync();
      }
      value() {
        return parseInt(this.input.value, 10) || this.min;
      }
      set(n) {
        n = Math.round(n / this.step) * this.step;
        n = Math.min(this.max, Math.max(this.min, n));
        this.input.value = n;
        this.sync();
      }
      sync() {
        const v = this.value();
        if (this.dec) this.dec.disabled = v <= this.min;
        if (this.inc) this.inc.disabled = v >= this.max;
      }
    });
  }

  /* ---- Variant-aware text (SKU line, purchasing rows) -------------------
     Re-renders its own content from the freshly fetched section HTML that
     product-variant-selector emits on every variant change. */
  if (!customElements.get('wgh-variant-text')) {
    customElements.define('wgh-variant-text', class extends Base {
      render() {
        this.listenTo('variantChange', (e, data) => {
          const key = this.getAttribute('key');
          const source = key
            ? data.html.querySelector(`wgh-variant-text[key="${key}"]`)
            : data.html.querySelector('wgh-variant-text');
          if (source) this.replaceChildren(...source.cloneNode(true).childNodes);
        });
      }
    });
  }

  /* ---- Theme editor: reveal the tab that holds a selected block --------- */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:block:select', (e) => {
      const tabs = e.target.closest('wgh-tabs');
      if (!tabs) return;
      const tab = e.target.closest('[role="tab"]');
      const panel = e.target.closest('[role="tabpanel"]');
      const target = tab || (panel && tabs.querySelector(`[aria-controls="${panel.id}"]`));
      if (!target) return;
      const index = tabs.tabs.indexOf(target);
      if (index >= 0) tabs.select(index);
    });
  }

  /* ---- Quote button ---------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wgh-quote]');
    if (!btn) return;
    e.preventDefault();
    const productForm = btn.closest('product-form');
    const variantId = productForm?.getAttribute('current-variant');
    const base = btn.getAttribute('data-text') || '';
    const sku = btn.getAttribute('data-sku') || '';
    btn.dataset.text = sku ? `${base} (${sku})` : base;
    if (variantId && btn.dataset.variantMap) {
      try {
        const map = JSON.parse(btn.dataset.variantMap);
        if (map[variantId]) btn.dataset.text = `${base} (${map[variantId]})`;
      } catch (err) { /* ignore malformed map */ }
    }
    if (window.RFQPopup && typeof window.RFQPopup.open === 'function') {
      window.RFQPopup.open(btn);
    } else if (typeof window.openRfqPopup === 'function') {
      window.openRfqPopup(btn);
    } else {
      window.location.hash = '#RFQForm';
    }
  });

  /* ---- Priced add-on checkbox ------------------------------------------
     The checkbox's own name/value is a cart line property, which is only a
     note. Blocks that name an add-on product get data-wgh-addon, and ticking
     one puts that product in the cart as its own line so the fee is real.
     Held at quantity 1 per cart: a liftgate is charged per shipment, not per
     unit, so a second tick elsewhere must not stack another fee. */
  const addonPending = new WeakSet();

  const addonStatus = (input, message) => {
    const el = input.closest('.wgh-checkbox')?.querySelector('[data-addon-status]');
    if (el) el.textContent = message || '';
  };

  /* theme.liquid sets window.routes.cart from {{ routes.cart }}, which is not a
     real Liquid route (it is routes.cart_url) and so renders empty. Derive the
     cart URL from cart_add_url instead, which is populated and keeps any
     locale prefix the store may add. */
  const addonCartUrl = () => (window.routes.cart_add_url || '/cart/add').replace(/\/add$/, '');

  const addonCartLine = async (variantId) => {
    const res = await fetch(`${addonCartUrl()}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!res.ok) throw new Error(`cart read failed: ${res.status}`);
    const cart = await res.json();
    return cart.items.find((item) => String(item.variant_id) === String(variantId));
  };

  const addonCartWrite = async (url, payload) => {
    const res = await fetch(`${url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) throw new Error(`cart write failed: ${res.status}`);
  };

  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-wgh-addon]');
    if (!input || addonPending.has(input)) return;
    const variantId = input.getAttribute('data-addon-variant');
    if (!variantId) return;

    const wanted = input.checked;
    addonPending.add(input);
    input.disabled = true;
    addonStatus(input, wanted ? 'Adding to your cart…' : 'Removing…');

    try {
      const line = await addonCartLine(variantId);
      if (wanted && !line) {
        const properties = {};
        const forTitle = input.getAttribute('data-addon-for');
        if (forTitle) properties['Requested for'] = forTitle;
        await addonCartWrite(window.routes.cart_add_url, {
          items: [{ id: Number(variantId), quantity: 1, properties }]
        });
      } else if (!wanted && line) {
        await addonCartWrite(window.routes.cart_change_url, { id: line.key, quantity: 0 });
      }
      addonStatus(input, wanted ? 'Added to your cart.' : '');
      document.dispatchEvent(new CustomEvent('updateCart', {
        bubbles: true,
        composed: true,
        detail: {}
      }));
      document.querySelector('main-cart')?.update?.();
    } catch (err) {
      input.checked = !wanted;
      addonStatus(input, 'Could not update your cart — please try again.');
      console.error(err);
    } finally {
      input.disabled = false;
      addonPending.delete(input);
    }
  });

  /* ---- Live chat trigger (Tidio, matching the theme's ask-expert card) -- */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-wgh-open-chat]');
    if (!trigger) return;
    e.preventDefault();
    const openTidio = () => {
      if (window.tidioChatApi) {
        window.tidioChatApi.show();
        window.tidioChatApi.open();
      }
    };
    if (window.tidioChatApi) openTidio();
    else document.addEventListener('tidioChat-ready', openTidio, { once: true });
  });
})();
